import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Store, Truck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";
import { useAuth } from "@/context/auth-context";
import { homeFor } from "@/components/RoleGuard";
import { Brand } from "@/components/common/Brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CenterSpinner } from "@/components/common/States";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { signInWithToken } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/invites/${token}`)
      .then(setInvite)
      .catch(() => setInvite({ status: "not_found", valid: false }))
      .finally(() => setLoading(false));
  }, [token]);

  const onGoogleSuccess = async (accessToken) => {
    if (!accessToken) return toast.error("Google sign-in failed");
    try {
      const res = await api("/auth/google/web", { method: "POST", json: { access_token: accessToken, invite_token: token } });
      await signInWithToken(res.session_token, res.user);
      toast.success(`Welcome — you're now a ${res.user.role}!`);
      navigate(homeFor(res.user.role), { replace: true });
    } catch (e) {
      toast.error(e.message || "Could not accept invite");
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center"><CenterSpinner /></div>;

  const roleLabel = invite?.role === "vendor" ? "Vendor" : "Driver";
  const RoleIcon = invite?.role === "vendor" ? Store : Truck;
  const invalidMsg = {
    expired: "This invite link has expired. Ask an admin to send a new one.",
    used: "This invite has already been used.",
    revoked: "This invite has been revoked.",
    not_found: "This invite link is invalid.",
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-popblue p-6">
      <div className="checker checker-red absolute inset-x-0 bottom-0 h-10 opacity-90" />
      <div className="stripe-green absolute inset-x-0 top-0 h-5" />
      <Card className="w-full max-w-md rounded-3xl p-8 shadow-pop" data-testid="invite-accept-card">
        <Brand />
        {invite?.valid ? (
          <>
            <div className="mt-6 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-popyellow shadow-pop-sm">
                <RoleIcon className="h-6 w-6 text-foreground" />
              </span>
              <div>
                <h1 className="font-display text-2xl font-bold" data-testid="invite-role-title">You're invited as a {roleLabel}</h1>
                <p className="text-sm text-muted-foreground">Sign in with Google to accept and set up your account.</p>
              </div>
            </div>
            {invite.email_locked && invite.email ? (
              <p className="mt-4 rounded-xl bg-muted p-3 text-sm text-muted-foreground">
                This invite is locked to <span className="font-medium text-foreground">{invite.email}</span> — sign in with that Google account.
              </p>
            ) : null}
            <div className="mt-6 flex justify-center" data-testid="invite-google-button">
              <GoogleButton onToken={onGoogleSuccess} className="rounded-full border-2 font-display" testId="invite-google-trigger" />
            </div>
          </>
        ) : (
          <div className="mt-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </span>
            <h1 className="mt-3 font-display text-2xl font-bold">Invite unavailable</h1>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="invite-invalid-msg">{invalidMsg[invite?.status] || "This invite link is invalid."}</p>
            <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/login")}>Go to sign in</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
